import React from 'react';
import { FiGlobe, FiCheckCircle, FiUsers, FiShield, FiBookOpen, FiBarChart2, FiCalendar } from 'react-icons/fi';

const PrivateUniversity = () => {
  const features = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Multiple Colleges Under One Roof',
      description: 'Private universities often have multiple constituent colleges—engineering, management, law, arts, sciences, and more. The ERP must handle the complexity of managing multiple colleges while providing a unified view for the university administration.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Diverse Program Offerings',
      description: 'Private universities offer a wide range of programs—UG, PG, doctoral, diploma, and certification programs across multiple disciplines. The system must support this diversity without forcing a one-size-fits-all approach.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Faculty and Staff Management',
      description: 'Managing faculty across multiple departments and colleges is complex. The ERP should handle faculty recruitment, workload distribution, performance evaluation, and professional development tracking.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Semester and Academic Calendar Management',
      description: 'Private universities often have different academic calendars for different programs. The system must manage multiple schedules, exam periods, and academic events without conflicts.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Accreditation and Compliance',
      description: 'Private universities must maintain accreditation from multiple bodies—UGC, NAAC, AICTE, and professional councils. The ERP should generate comprehensive reports for all accreditation requirements.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Research and Innovation Tracking',
      description: 'Private universities are hubs of research and innovation. The system should track research projects, publications, patents, and collaborations. This supports university rankings and accreditation.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Unified View Across Colleges',
      description: 'See all your constituent colleges from one dashboard. Compare performance and make strategic decisions.'
    },
    {
      title: 'Flexible Program Management',
      description: 'Support diverse programs without forcing rigid structures. Design programs that meet industry needs.'
    },
    {
      title: 'Enhanced Student Experience',
      description: 'Provide students with a seamless experience—from admission to graduation. Access to grades, attendance, and services.'
    },
    {
      title: 'Accreditation Readiness',
      description: 'Generate reports for NAAC, NIRF, UGC, and professional councils automatically. Maintain quality standards.'
    },
    {
      title: 'Research Management',
      description: 'Track research output, funding, and collaborations. Build your university\'s research reputation.'
    },
    {
      title: 'Data-Driven Decisions',
      description: 'Make strategic decisions based on real data. Understand what works and what needs improvement.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Private universities are transforming Indian higher education. They bring innovation, industry connections, and a focus on student outcomes. With their autonomy and resources, they can create programs that prepare students for the future.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        However, private universities are also complex organizations. <strong className="text-slate-900">Multiple colleges. Diverse programs. Thousands of students. Complex administration. Accreditation requirements. Research management.</strong> The scale and complexity require sophisticated systems designed specifically for universities, not just scaled-up college software.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Digitization is transforming private universities. Here's how modern university management systems are making a difference.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">400+</div>
          <div className="text-sm text-slate-500">Private universities in India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">1.5 Crore+</div>
          <div className="text-sm text-slate-500">Students enrolled in private universities</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">50%</div>
          <div className="text-sm text-slate-500">Faster operations with digitization</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Key Features of a University Management System</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Digitization Delivers for Private Universities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Digitized University Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Approach</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Manual processes and paperwork</span></li>
              <li className="flex items-start gap-2">• <span>Separate systems for different functions</span></li>
              <li className="flex items-start gap-2">• <span>Slow decision-making</span></li>
              <li className="flex items-start gap-2">• <span>Limited visibility across colleges</span></li>
              <li className="flex items-start gap-2">• <span>Time-consuming accreditation reporting</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Digitized Approach with ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Automated workflows and digital records</span></li>
              <li className="flex items-start gap-2">• <span>Integrated platform for all functions</span></li>
              <li className="flex items-start gap-2">• <span>Real-time visibility across all operations</span></li>
              <li className="flex items-start gap-2">• <span>Complete view of all constituent colleges</span></li>
              <li className="flex items-start gap-2">• <span>Automated accreditation report generation</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Private Universities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🏛️</div>
            <h4 className="font-bold text-slate-900 text-sm">Multi-College Management</h4>
            <p className="text-xs text-slate-600">One platform for all constituent colleges</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Diverse Programs</h4>
            <p className="text-xs text-slate-600">Support UG, PG, doctoral, and certification programs</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔬</div>
            <h4 className="font-bold text-slate-900 text-sm">Research Management</h4>
            <p className="text-xs text-slate-600">Track research projects, publications, and collaborations</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Accreditation Ready</h4>
            <p className="text-xs text-slate-600">NAAC, NIRF, UGC, and professional council reports</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👨‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Faculty Management</h4>
            <p className="text-xs text-slate-600">Workload tracking and professional development</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Student-Centric</h4>
            <p className="text-xs text-slate-600">Seamless student experience from admission to graduation</p>
          </div>
        </div>
      </div>

      {/* The Digital Transformation Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Digital Transformation Journey for Private Universities</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 1</h4>
            <p className="text-xs text-slate-600">Assess current systems and processes</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 2</h4>
            <p className="text-xs text-slate-600">Implement integrated ERP platform</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">🔄</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 3</h4>
            <p className="text-xs text-slate-600">Automate workflows and processes</p>
          </div>
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">Step 4</h4>
            <p className="text-xs text-slate-600">Leverage data for strategic decisions</p>
          </div>
        </div>
      </div>

      {/* The Bigger Picture */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Why Digitization Matters for Private Universities</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Private universities face unique challenges—managing multiple colleges, diverse programs, faculty across departments, research activities, and accreditation requirements. Without integrated systems, these challenges become administrative nightmares.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          Digitization transforms this complexity into efficiency. It gives leadership real-time visibility into operations. It frees faculty and staff from administrative burden. It improves student experience from admission to graduation. It builds the data foundation for strategic growth.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Your university's reputation depends on quality. Quality depends on systems. Systems depend on the right ERP.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Digitize Your Private University with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Private universities are at the forefront of Indian higher education. They have the autonomy, resources, and ambition to create world-class education. But they need systems that match their scale and complexity.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon provides a university management system designed for private universities. Multiple colleges. Diverse programs. Faculty management. Research tracking. Accreditation reporting. All on one integrated platform.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for private universities.</strong> Built for scale. Built for complexity. Built for excellence.
        </p>
      </div>
    </>
  );
};

export default PrivateUniversity;